import { LoadingSkeleton } from '@bloomfieldrobotics/canopy';
import { Box, styled } from '@mui/material';
import { useMemo, useState } from 'react';
import { useFeatureFlags } from '../../../../../services/featureFlags/hooks/useFeatureFlags';
import { ImageResolution } from '../../../../../services/images/hooks/useColorCorrection/types';
import { useColorCorrection } from '../../../../../services/images/hooks/useColorCorrection/useColorCorrection';

type PointImageProps = {
  /**
   * The source URL for the image to display
   */
  src?: string;
  /**
   * Alt text for the image
   * @default 'Displayed Image'
   */
  alt?: string;
  /**
   * Minimum height of the image container
   * @default 240
   */
  minHeight?: number;
  /**
   * Maximum height of the image container
   * @default 240
   */
  maxHeight?: number;
  /**
   * Border radius for the image
   * @default '8px'
   */
  borderRadius?: string;
  /**
   * Whether to apply color correction to the image
   * @default false
   */
  enableColorCorrection?: boolean;
  /**
   * The image resolution in pixels for color correction processing.
   * Used to determine appropriate memory thresholds for worker respawning.
   * Higher resolutions (4K: 2160, 8K: 4320) will use higher memory thresholds.
   * @default '720'
   */
  imageResolution?: ImageResolution;
};

/**
 * PointImage Component
 *
 * A reusable component for displaying point images with loading states.
 * Uses the useImage hook for better image loading and supports color correction.
 * Handles image loading, error states, and provides a consistent layout.
 */
export function PointImage({
  src,
  alt = 'Displayed Image',
  minHeight = 240,
  maxHeight = 240,
  borderRadius = '8px',
  enableColorCorrection = true,
  imageResolution = '720',
}: PointImageProps) {
  const { ColorCorrectionToggle } = useFeatureFlags();
  const [showColorCorrection, setShowColorCorrection] = useState(true);

  // Load the original image using useImage hook

  const { finalImage, colorCorrectionState } = useColorCorrection(src, {
    imageResolution,
  });

  /**
   * Color correction service loading state
   */
  const isColorCorrectionLoading =
    enableColorCorrection && colorCorrectionState.isLoading;
  const isColorCorrectionError =
    enableColorCorrection && colorCorrectionState.error;

  const displayImageSrc = useMemo(() => {
    if (isColorCorrectionLoading || !finalImage?.src) {
      return src;
    }
    return finalImage.src;
  }, [src, finalImage?.src, isColorCorrectionLoading]);

  return (
    <ImageContainer maxHeight={maxHeight} minHeight={minHeight}>
      <LoadingSkeleton
        loading={isColorCorrectionLoading}
        variant='rectangular'
        sx={(theme) => ({
          borderRadius: theme.vars.shape.borderRadius,
          minHeight: minHeight,
        })}
        height='100%'
        width='100%'
      >
        <ImageWrapper
          minHeight={minHeight}
          onMouseDown={() =>
            ColorCorrectionToggle && setShowColorCorrection(false)
          }
          onMouseUp={() =>
            ColorCorrectionToggle && setShowColorCorrection(true)
          }
        >
          {isColorCorrectionError ? (
            <ErrorPlaceholder minHeight={minHeight}>
              <span>Failed to load image</span>
            </ErrorPlaceholder>
          ) : (
            <>
              <img
                src={displayImageSrc}
                alt={alt}
                style={{
                  width: '100%',
                  borderRadius,
                  objectFit: 'contain',
                  display: ColorCorrectionToggle
                    ? showColorCorrection
                      ? 'block'
                      : 'none'
                    : 'block',
                }}
              />
              {ColorCorrectionToggle && (
                <img
                  src={src}
                  alt={alt}
                  style={{
                    width: '100%',
                    borderRadius,
                    objectFit: 'contain',
                    display: showColorCorrection ? 'none' : 'block',
                  }}
                />
              )}
            </>
          )}
        </ImageWrapper>
      </LoadingSkeleton>
    </ImageContainer>
  );
}

const ImageContainer = styled(Box)<{ maxHeight: number; minHeight: number }>(
  ({ maxHeight, minHeight }) => ({
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight,
    minHeight,
  })
);

const ImageWrapper = styled(Box)<{ minHeight: number }>(({ minHeight }) => ({
  display: 'flex',
  minHeight,
  position: 'relative',
  height: '100%',
}));

const ErrorPlaceholder = styled(Box)<{ minHeight: number }>(
  ({ theme, minHeight }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: minHeight,
    backgroundColor: theme.vars.palette.grey[100],
    color: theme.vars.palette.text.secondary,
    borderRadius: '8px',
    fontSize: '14px',
  })
);
